import React from 'react';
import { useRouter } from 'next/router';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useMantineTheme } from '@mantine/core';
import { useMediaQuery } from 'react-responsive';

import { Routes } from '~/domains/common/constants/routes';
import { ButtonLink } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { LoginHeader } from '~/domains/auth/components/LoginHeader/LoginHeader';
import type { BRANDS, PLATFORMS } from '~/domains/auth/consts';
import dt from '~/testing';

import { RecaptchaText } from '../RecaptchaText';

import SignInForm, { type SignInFormProps } from './SignInForm';
import useStyles from './SignInActions.styles';

type SignInActionProps = {
  signInProps: SignInFormProps;
  brand?: BRANDS;
  platform?: PLATFORMS;
  resetSuccess?: string;
};

function SignInActions({ brand, platform, signInProps, resetSuccess }: SignInActionProps) {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const { t } = useTranslation('auth');
  const router = useRouter();
  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.sm });

  const ssoStatus = router.query.status;
  const ssoUnifiedLoginAttempted = ssoStatus === 'authSSODeprecated';

  return (
    <div className={classes.container}>
      <LoginHeader
        title={t('signIn.title')}
        logoSize={30}
        titleVariant="display-headline-expanded-xxl"
        isLogoVisible={!isMobile}
        centered
        brand={brand}
        platform={platform}
        resetSuccess={resetSuccess}
        ssoUnifiedLoginAttempted={ssoUnifiedLoginAttempted}
      />

      <SignInForm {...signInProps} />

      <Text
        variant="body-large"
        ta="center"
        color={theme.colors.gray2[4]}
        className={classes.textWithLink}
      >
        <Trans
          i18nKey="auth:signIn.signUpCTA"
          components={{
            signUpLink: (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore Children are injected via <Trans> component
              <ButtonLink
                data-test-id={dt.components.shared.auth.signIn.signUpLink}
                className={classes.buttonLink}
                mode="linkButton"
                size="large"
                type="text"
                href={Routes.signUp({
                  redirectTo: router.query.redirectTo as string,
                  referralCode: router.query.referralCode as string,
                  brand: router.query.brand as string,
                  platform: router.query.platform as string,
                })}
              />
            ),
          }}
        />
      </Text>

      <RecaptchaText />
    </div>
  );
}

export default SignInActions;
